/**
 * Admin · Audit logs — paginated, searchable consent_log feed.
 *
 * Phase 1: load up to 200 rows and let the admin filter client-side by action
 * prefix. Future iterations can add pagination via a `cursor` param.
 */
import { useEffect, useMemo, useState } from "react";
import { Search, History } from "lucide-react";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty } from "./_shared";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function AdminAuditLogs() {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!workspace) return;
    let alive = true;
    api
      .get(`/admin/workspaces/${workspace.id}/audit-log?limit=200`)
      .then((r) => alive && setLogs(r.data || []))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [workspace?.id]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter(
      (l) =>
        (l.action || "").toLowerCase().includes(q) ||
        (l.user_name || "").toLowerCase().includes(q) ||
        (l.user_role || "").toLowerCase().includes(q)
    );
  }, [logs, search]);

  if (wsLoading) return null;
  if (!workspace)
    return (
      <AdminEmpty testid="admin-audit-no-ws">
        No workspace yet.
      </AdminEmpty>
    );

  return (
    <div data-testid="admin-audit-screen">
      <AdminHeader
        eyebrow="Audit logs"
        title="Everything the platform remembers"
      />
      <div className="mt-2 text-sm text-[#6B7280] max-w-2xl">
        Each sensitive action is calmly recorded: who did it, what changed,
        when, and from which role.
      </div>

      <div className="mt-6 flex items-center gap-2 bg-white border hairline rounded-full px-4 py-2 max-w-md">
        <Search size={14} strokeWidth={1.5} className="text-[#6B7280]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search action, user, or role…"
          data-testid="admin-audit-search"
          className="bg-transparent outline-none text-sm flex-1"
        />
      </div>

      {loading ? (
        <AdminEmpty testid="admin-audit-loading">A quiet moment…</AdminEmpty>
      ) : visible.length === 0 ? (
        <AdminEmpty testid="admin-audit-empty">
          No entries match.
        </AdminEmpty>
      ) : (
        <ul
          data-testid="admin-audit-list"
          className="mt-6 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {visible.map((l) => (
            <li
              key={l.id}
              data-testid={`admin-audit-row-${l.id}`}
              className="px-5 py-4 flex items-start gap-4"
            >
              <span
                className="h-9 w-9 rounded-2xl bg-[#F7F8FC] flex items-center justify-center shrink-0 mt-0.5"
                aria-hidden
              >
                <History
                  size={15}
                  strokeWidth={1.6}
                  className="text-[#1A1B2E]"
                />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline flex-wrap gap-2">
                  <span className="font-serif text-base text-[#1A1B2E]">
                    {l.user_name || "—"}
                  </span>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#6B7280]">
                    {l.user_role || "—"}
                  </span>
                  <span className="text-sm text-[#6B7280] font-medium">
                    · {l.action}
                  </span>
                </div>
                {l.target_id && (
                  <div
                    className="text-xs text-[#6B7280] mt-1"
                    data-testid={`admin-audit-row-${l.id}-target`}
                  >
                    target: {l.target_id}
                  </div>
                )}
                {l.details && Object.keys(l.details).length > 0 && (
                  <pre
                    className="text-xs text-[#6B7280] mt-2 bg-[#F7F8FC]/60 rounded-xl px-3 py-2 overflow-x-auto"
                    data-testid={`admin-audit-row-${l.id}-details`}
                  >
                    {JSON.stringify(l.details, null, 0)}
                  </pre>
                )}
              </div>
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#6B7280] shrink-0">
                {fmt(l.at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
